import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CatalogSourceOut, CatalogSourcePatch, LicenseStatus } from '@/api/types';
import { qk } from '@/api/queries';
import { Button, Card, Drawer, FieldHint, IconButton, Input, Pill, Segmented, Textarea, useToast, type Tone } from '@/design-system';
import { now } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { daysUntil, formatDateShort, formatNumber, formatRelative } from '@/lib/format';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { catalogSourcesQuery, patchCatalogSource, syncCatalogSource } from './api';
import s from './settings.module.css';

/** A source refuses to run when its terms were checked longer ago than this (S7, catalog.py `gate`). */
export const TERMS_MAX_AGE_DAYS = 90;

const LICENSE_TONE: Record<LicenseStatus, Tone> = { approved: 'ok', unreviewed: 'warn', rejected: 'err' };

export function termsFresh(checkedAt: string | null, at: Date = now()): boolean {
  return !!checkedAt && -daysUntil(checkedAt, at) <= TERMS_MAX_AGE_DAYS;
}

type Blocker = 'manual' | 'on_demand' | 'not_syncable' | 'license' | 'disabled' | 'terms';

/**
 * Why a source cannot sync now, or null. Only an API registry syncs in bulk; 21st.dev is on demand
 * and motionsites manual (D24); then the licence gate (enabled, approved, terms ≤ 90 days). The API
 * enforces the same (409).
 */
export function syncBlocker(src: CatalogSourceOut, at: Date = now()): Blocker | null {
  if (src.mode === 'manual') return 'manual';
  if (src.kind === '21st') return 'on_demand';
  if (src.kind !== 'registry') return 'not_syncable';
  if (src.license_status !== 'approved') return 'license';
  if (!src.enabled) return 'disabled';
  if (!termsFresh(src.terms_checked_at, at)) return 'terms';
  return null;
}

type ModeKey = 'manual' | 'on_demand' | 'scheduled' | 'api';
function modeKey(src: CatalogSourceOut): ModeKey {
  if (src.mode === 'manual') return 'manual';
  if (src.kind === '21st') return 'on_demand';
  return src.sync_schedule ? 'scheduled' : 'api';
}

const host = (url: string | null) => (url ? (url.replace(/^https?:\/\//, '').split('/')[0] ?? url) : '');

/** «Каталоги дизайна» (P3-E04, additive; DESIGN_SPEC §9 C7; PLAN D24): the catalog sources, their licences and syncs. */
export function CatalogSources() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const q = useQuery(catalogSourcesQuery);
  const [editing, setEditing] = useState<string | null>(null);
  const sync = useMutation({
    mutationFn: (src: CatalogSourceOut) => syncCatalogSource(src.id),
    onSuccess: (_job, src) => toast(t('catalogs.sync_started', { name: src.name })),
    onError: (err) => toast(errorText(err, tc)),
  });
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const at = now();
  const current = q.data.find((x) => x.id === editing);
  return (
    <>
      <Card padding={8}>
        <div className={s.intro}>{t('catalogs.intro', { days: TERMS_MAX_AGE_DAYS })}</div>
        {q.data.map((src) => {
          const blocker = syncBlocker(src, at);
          const fresh = termsFresh(src.terms_checked_at, at);
          const n = formatNumber(src.presets_count);
          return (
            <div key={src.id} className={s.catalogRow} data-testid={`catalog-${src.name}`}>
              <div className={s.catalogName}>
                <span className={s.mcpName}>{src.name}</span>
                <span className={s.monoSub}>
                  {host(src.base_url)} · {t(`catalogs.mode.${modeKey(src)}`)}
                </span>
              </div>
              <div className={s.catalogState}>
                <span>
                  {src.health.error
                    ? t('catalogs.health.error', { detail: src.health.error })
                    : src.last_synced_at
                      ? t('catalogs.health.synced', { when: formatRelative(src.last_synced_at, at, true), count: src.presets_count, n })
                      : src.presets_count
                        ? t('catalogs.health.imported', { count: src.presets_count, n })
                        : t('catalogs.health.never')}
                </span>
                <span className={cx(!fresh && s.warnText)}>
                  {src.terms_checked_at ? t(fresh ? 'catalogs.terms_checked' : 'catalogs.terms_stale', { date: formatDateShort(src.terms_checked_at) }) : t('catalogs.terms_never')}
                </span>
              </div>
              <Pill tone={LICENSE_TONE[src.license_status]} size="tagTight" className={s.catalogPill}>
                {t(`catalogs.license.${src.license_status}`)}
              </Pill>
              <div className={s.catalogActions}>
                <Button
                  variant="secondary"
                  size={32}
                  icon="refresh-cw"
                  iconSize={13}
                  disabled={!!blocker || sync.isPending}
                  title={blocker ? t(`catalogs.blocked.${blocker}`) : undefined}
                  onClick={() => sync.mutate(src)}
                >
                  {t('catalogs.sync')}
                </Button>
                <IconButton icon="pencil" label={t('catalogs.edit', { name: src.name })} size={32} iconSize={14} onClick={() => setEditing(src.id)} />
              </div>
            </div>
          );
        })}
      </Card>
      {current && <CatalogDrawer source={current} onClose={() => setEditing(null)} />}
    </>
  );
}

/** The licence review (approve or reject, notes, attribution), the terms check, the schedule and the switch. */
export function CatalogDrawer({ source, onClose }: { source: CatalogSourceOut; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const src = source;
  const [license, setLicense] = useState<LicenseStatus>(src.license_status);
  const [notes, setNotes] = useState(src.license_notes ?? '');
  const [attribution, setAttribution] = useState(src.attribution_template ?? '');
  const [termsChecked, setTermsChecked] = useState(false);
  const [schedule, setSchedule] = useState(src.sync_schedule ?? 'off');
  const [enabled, setEnabled] = useState(src.enabled);
  const [problem, setProblem] = useState<string | null>(null);
  const scheduled = src.mode === 'api' && src.kind === 'registry';
  const mode = modeKey(src);

  const save = useMutation({
    mutationFn: () => {
      const body: CatalogSourcePatch = {};
      if (license !== src.license_status) body.license_status = license;
      if (notes.trim() !== (src.license_notes ?? '')) body.license_notes = notes.trim() || null;
      if (attribution.trim() !== (src.attribution_template ?? '')) body.attribution_template = attribution.trim() || null;
      if (termsChecked) body.terms_checked = true;
      const nextSchedule = schedule === 'off' ? null : schedule;
      if (scheduled && nextSchedule !== src.sync_schedule) body.sync_schedule = nextSchedule;
      if (enabled !== src.enabled) body.enabled = enabled;
      return patchCatalogSource(src.id, body);
    },
    onSuccess: (next) => {
      qc.setQueryData<CatalogSourceOut[]>(qk.catalogSources, (list) => list?.map((x) => (x.id === next.id ? next : x)));
      toast(next.license_status === 'approved' && src.license_status !== 'approved' ? t('catalogs.approved', { name: next.name }) : t('catalogs.saved', { name: next.name }));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    save.mutate();
  };

  const checkedAt = termsChecked ? now().toISOString() : src.terms_checked_at;
  const fresh = termsFresh(checkedAt);

  return (
    <Drawer
      open
      onClose={onClose}
      title={src.name}
      sub={t('catalogs.drawer_sub', { mode: t(`catalogs.mode_long.${mode}`) })}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="catalog-settings" disabled={save.isPending}>
            {t('catalogs.save')}
          </Button>
        </div>
      }
    >
      <form id="catalog-settings" className={s.drawerForm} onSubmit={submit} noValidate>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('catalogs.license_label')}</span>
          <Segmented
            variant="form"
            label={t('catalogs.license_label')}
            value={license}
            onChange={setLicense}
            className={s.alignStart}
            options={(['unreviewed', 'approved', 'rejected'] as const).map((k) => ({ value: k, label: t(`catalogs.license_set.${k}`) }))}
          />
          <FieldHint>{t('catalogs.license_hint')}</FieldHint>
        </div>
        <Textarea label={t('catalogs.notes')} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        <div className={s.group}>
          <Input label={t('catalogs.attribution')} mono value={attribution} onChange={(e) => setAttribution(e.target.value)} />
          <FieldHint>{t('catalogs.attribution_hint')}</FieldHint>
        </div>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('catalogs.terms')}</span>
          <div className={s.termsRow}>
            <span className={cx(s.secretState, !fresh && s.warnText)} data-testid="terms-state">
              {checkedAt ? t(fresh ? 'catalogs.terms_checked' : 'catalogs.terms_stale', { date: formatDateShort(checkedAt) }) : t('catalogs.terms_never')}
            </span>
            {src.terms_url && (
              <a href={src.terms_url} target="_blank" rel="noreferrer noopener" className={s.link}>
                {t('catalogs.terms_open')}
              </a>
            )}
          </div>
          <Button variant="secondary" size={34} icon="check" iconSize={14} className={s.alignStart} disabled={termsChecked} onClick={() => setTermsChecked(true)}>
            {t('catalogs.terms_mark')}
          </Button>
        </div>
        {scheduled && (
          <div className={s.group}>
            <span className={s.groupLabel}>{t('catalogs.schedule')}</span>
            <Segmented
              variant="form"
              label={t('catalogs.schedule')}
              value={schedule}
              onChange={setSchedule}
              className={s.alignStart}
              options={(['off', 'daily', 'weekly'] as const).map((k) => ({ value: k, label: t(`catalogs.schedules.${k}`) }))}
            />
          </div>
        )}
        <div className={s.group}>
          <span className={s.groupLabel}>{t('catalogs.enabled')}</span>
          <Segmented
            variant="form"
            label={t('catalogs.enabled')}
            value={enabled ? 'on' : 'off'}
            onChange={(v) => setEnabled(v === 'on')}
            className={s.alignStart}
            options={[
              { value: 'on', label: t('catalogs.enabled_on') },
              { value: 'off', label: t('catalogs.enabled_off') },
            ]}
          />
          <FieldHint>{license === 'approved' && fresh ? t(`catalogs.enabled_hint.${mode}`) : t('catalogs.enable_needs_license')}</FieldHint>
        </div>
      </form>
    </Drawer>
  );
}
